import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Col, Container, Row } from 'react-bootstrap';
import CircledIcon from '../../../components/CircledIcon';
import useMobile from '../../../hooks/useMobileHook';
import GenericTable, { tableDataProps } from '../../../components/GenericTable';
import { formatPrice } from '../../../utils/utils';
import IconButton from '../../../components/IconButton';
import { useState } from 'react';
import DefaultCard from '../../../components/DefaultCard';
import Button from '../../../components/Button';
import { getLifeAndDisabilityInsuranceDescription, getLifeAndDisabilityInsuranceDescriptionTitles } from './info/LifeAndDisabilityInsuranceInfo';
import Link from '../../../components/Link';

export type customLifeAndDisabilityInsuranceTabProps = {
  lifeAndDisabilityInsurances: any;
  headerTitle?: String;
};

const LifeAndDisabilityInsuranceTab = ({
  lifeAndDisabilityInsurances,
  headerTitle,
}: customLifeAndDisabilityInsuranceTabProps) => {

  const isMobile = useMobile();
  const [showDetails, setShowDetails] = useState<any>({
    baselife: false,
    optionallife: false,
    spouselife: false,
    longtermdisability: false,
  });

  const toggleDetails = (type: String) => {
    if (type !== 'long-term-disability') return;
    let key = type.replaceAll('-', '');
    let value = showDetails[`${key}`];
    setShowDetails({...showDetails, [key]: !value});
  };

  const [tellMeMore, setTellMeMore] = useState<any>({
    baselife: false,
    optionallife: false,
    spouselife: false,
    longtermdisability: false,
  });

  const handleTellMeMore = (type: String) => {
    let key = type.replaceAll('-', '');
    let value = tellMeMore[`${key}`];
    setTellMeMore({...tellMeMore, [key]: !value});
  };

  const getTableData = (plans: Array<any>) => {
    let tableData: tableDataProps = {
      headers: ['Option', 'Coverage Amount', 'Per Pay Period Cost'],
      rows: []
    };

    plans.map( (plan, i) => {
      tableData = plan['id'].includes('oli') || plan['id'].includes('ltd') ? {
        ...tableData,
        rows: [...tableData['rows'],
          [
            plan.title,
            plan?.coverage && formatPrice(plan?.coverage),
            plan.costType === 'credit' ? `credit (${formatPrice(Math.abs(plan.cost))})` : formatPrice(plan.cost),
          ]
        ],
      } : {
        ...tableData,
        headers: ['Option', 'Per Pay Period Cost'],
        rows: [...tableData['rows'],
          [
            plan.title,
            plan.costType === 'credit' ? `credit (${formatPrice(Math.abs(plan.cost))})` : formatPrice(plan.cost),
          ],
        ]
      };
      return plan;
    });
    return tableData;
  }

  const getCellCoordinates = (benefit: any) => {
    let y = 1;
    let x = 1;
    if ( benefit['plan'].id.includes('ltd') ) x = 2;
    benefit['availablePlans'].map(
      (b: any, i: number) => {
        if (b.id === benefit['plan'].id) y = i;
        return b;
      }
    );    
    return [y,x];
  }

  const getSelectedRow = (benefit: any) => {
    if (benefit['type'] !== 'optional-life') return undefined;
    return benefit['plan'].id === 'hsa-noCoverage' ? 1 : 0;
  }

  const icon = (iconName: IconProp) => (
    <CircledIcon
      variant={'icon-emphasized'}
      size={isMobile ? 'icon-sm' : 'icon-lg'}
      icon={iconName}
    />
  );

  const getCardIcon = (type: String) => {
    switch (type) {
      case 'hospitals':
        return light('hospitals');
      default:
        return light('image-slash');
    }
  };

  return (
    <Container className={'px-0'}>
      <Row className={`align-items-center ${headerTitle ? 'mt-8' : ''}`}>
        <Col className={'col-12 col-lg-6'}>
          {headerTitle && <span className={'text-sm-16-lg-24 fw-normal lx-xxs'}>
            {headerTitle}
          </span>}
        </Col>
        <div>
          {lifeAndDisabilityInsurances.map((benefit: any, index: number) => {
            return (
              <Container className={`m-0 ${index || headerTitle ? 'mt-10' : ''} p-10 border border-info rounded-1`}>
                <div className={'d-flex justify-content-between align-items-center mb-10'}>
                  <Col>
                    {icon(getCardIcon(benefit['generic'].icon))}
                    <span className={'h5 mb-0 ms-4'}>{benefit['generic'].title}</span>
                  </Col>
                  {benefit['type'] === 'long-term-disability' && <div onClick={() => toggleDetails(benefit['type'])} className={'icon-button-wrapper'}>
                    <IconButton
                      variant="icon-default"
                      icon={light('circle-exclamation')}
                      extLabel="Benefit Info"
                    />
                  </div>}
                </div>
                <Row className={`expandable-wrapper ${showDetails[benefit['type'].replaceAll('-', '')] ? 'open' : ''}`}>
                  <div className="d-flex justify-content-center">
                    <DefaultCard
                      closeButton={
                        <Link 
                          className='pt-0' 
                          isAlignAtEnd 
                          isWhiteIcon 
                          rightIcon={light('close')}
                          onClick={() => toggleDetails(benefit['type'])}
                        />                      
                      }
                      variant="blue-motif"
                      leftHeading={
                        getLifeAndDisabilityInsuranceDescriptionTitles(benefit['type'])
                      }
                      leftSubheading={
                        <Container className={'px-0 d-flex flex-column'}>
                          <span>
                            {getLifeAndDisabilityInsuranceDescription(benefit['type'], tellMeMore[`${benefit['type'].replaceAll('-', '')}`])}
                          </span>
                        </Container>
                      }
                      button={
                        <Button
                          onClick={() => handleTellMeMore(benefit['type'])}
                          variant="primary-shadow"
                          size="sm"
                        >
                          {tellMeMore[`${benefit['type'].replaceAll('-', '')}`] ? 'Tell me less' : 'Tell me more'}
                        </Button>
                      }
                      className={'mb-12'}
                    />
                  </div>
                </Row>
                <Col className={'mt-10 overflow-auto'}>
                  <GenericTable
                    selectedRowIndex={getSelectedRow(benefit)}
                    selectedCellPosition={benefit['type'] === 'optional-life' ? undefined : getCellCoordinates(benefit)}
                    tableData={getTableData(benefit['availablePlans'])}
                  />
                </Col>
              </Container>
            );
          })}
        </div>
      </Row>
    </Container>
  );
};

export default LifeAndDisabilityInsuranceTab;
